import React, { useState, useEffect } from 'react';
import {
  Modal,
  Card,
  CardContent,
  Grid,
  TextField,
  Button,
  FormControlLabel,
  Checkbox,
  Typography,
  Paper,
  Autocomplete,
} from '@mui/material';
import { DateTimePicker } from '@mui/lab';
import axios from '../../CustomAxios';
import { debounce } from 'lodash';

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 550,
  bgcolor: 'background.paper',
  boxShadow: 24,
  p: 4,
};

export default function Filter({ open, onClose, onApply, initialValues }) {
  const authData = JSON.parse(localStorage.getItem('AuthData'));

  // Filter states (AP Company removed)
  const [jobId, setJobId] = useState(initialValues.jobId || '');
  const [challanId, setChallanId] = useState(initialValues.challanId || '');
  const [type, setType] = useState(initialValues.type || '');
  const [fromDate, setFromDate] = useState(
    initialValues.fromDate ? new Date(initialValues.fromDate) : null
  );
  const [toDate, setToDate] = useState(
    initialValues.toDate ? new Date(initialValues.toDate) : null
  );
  const [plateId, setPlateId] = useState(initialValues.plateId || '');
  const [customerCompanyId, setCustomerCompanyId] = useState(
    initialValues.customerCompanyId || ''
  );

  // Data for autocomplete
  const [jobsList, setJobsList] = useState([]);
  const [challansList, setChallansList] = useState([]);
  const [platesList, setPlatesList] = useState([]);
  const [customerCompaniesList, setCustomerCompaniesList] = useState([]);

  const [searchJobText, setSearchJobText] = useState('');
  const [searchChallanText, setSearchChallanText] = useState('');
  const [searchPlateText, setSearchPlateText] = useState('');
  const [searchCustomerCompanyText, setSearchCustomerCompanyText] = useState('');

  // Fetch functions (AP Company removed)
  const fetchJobs = async (search = '') => {
    try {
      const config = { headers: { Authorization: `Bearer ${authData.access_token}` } };
      const url = `/api/v1/${authData.type}/jobs?limit=5&search_keyword=${search}&is_table=true`;
      const { data } = await axios.get(url, config);
      if (data.success) setJobsList(data.data.data || data.data);
    } catch (e) {
      console.error('Error fetching jobs', e);
    }
  };

  const fetchChallans = async (search = '') => {
    try {
      const config = { headers: { Authorization: `Bearer ${authData.access_token}` } };
      const url = `/api/v1/${authData.type}/challan?limit=5&challan_search=${search}`;
      const { data } = await axios.get(url, config);
      if (data.success) setChallansList(data.data.data);
    } catch (e) {
      console.error('Error fetching challans', e);
    }
  };

  const fetchPlates = async (search = '') => {
    try {
      const config = { headers: { Authorization: `Bearer ${authData.access_token}` } };
      const url = `/api/v1/${authData.type}/plate_inventory?page=1&limit=5&search=${search}`;
      const { data } = await axios.get(url, config);
      if (data.success) setPlatesList(data.data.data);
    } catch (e) {
      console.error('Error fetching plates', e);
    }
  };

  const fetchCustomerCompanies = async (search = '') => {
    try {
      const config = { headers: { Authorization: `Bearer ${authData.access_token}` } };
      const url = `/api/v1/${authData.type}/customer_company?limit=5&search_keyword=${search}`;
      const { data } = await axios.get(url, config);
      if (data.success) setCustomerCompaniesList(data.data.data);
    } catch (e) {
      console.error('Error fetching customer companies', e);
    }
  };

  const debouncedFetchJobs = debounce(fetchJobs, 300);
  const debouncedFetchChallans = debounce(fetchChallans, 300);
  const debouncedFetchPlates = debounce(fetchPlates, 300);
  const debouncedFetchCustomerCompanies = debounce(fetchCustomerCompanies, 300);

  useEffect(() => {
    fetchJobs();
    fetchChallans();
    fetchPlates();
    fetchCustomerCompanies();
  }, []);

  useEffect(() => {
    if (searchJobText) debouncedFetchJobs(searchJobText);
    else fetchJobs();
  }, [searchJobText]);

  useEffect(() => {
    if (searchChallanText) debouncedFetchChallans(searchChallanText);
    else fetchChallans();
  }, [searchChallanText]);

  useEffect(() => {
    if (searchPlateText) debouncedFetchPlates(searchPlateText);
    else fetchPlates();
  }, [searchPlateText]);

  useEffect(() => {
    if (searchCustomerCompanyText)
      debouncedFetchCustomerCompanies(searchCustomerCompanyText);
    else fetchCustomerCompanies();
  }, [searchCustomerCompanyText]);

  const handleApply = () => {
    onApply({
      jobId: jobId || '',
      challanId: challanId || '',
      type: type || '',
      fromDate: fromDate ? new Date(fromDate).toISOString() : '',
      toDate: toDate ? new Date(toDate).toISOString() : '',
      plateId: plateId || '',
      customerCompanyId: customerCompanyId || '',
      // apCompanyId removed
    });
  };

  const handleReset = () => {
    setJobId('');
    setChallanId('');
    setType('');
    setFromDate(null);
    setToDate(null);
    setPlateId('');
    setCustomerCompanyId('');
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Card sx={style}>
        <CardContent>
          <Paper sx={{ p: 2 }}>
            <Grid container spacing={3}>
              {/* Job Autocomplete */}
              <Grid item xs={12} sm={6}>
                <Autocomplete
                  options={jobsList}
                  getOptionLabel={(option) => `${option.job_id} - ${option.name}`}
                  filterOptions={(options, { inputValue }) => {
                    if (!inputValue) return options.slice(0, 5);
                    const term = inputValue.toLowerCase();
                    return options.filter(
                      (opt) =>
                        opt.job_id.toLowerCase().includes(term) ||
                        opt.name.toLowerCase().includes(term)
                    );
                  }}
                  value={jobsList.find((j) => j._id === jobId) || null}
                  onChange={(e, newValue) => setJobId(newValue?._id || '')}
                  onInputChange={(e, newInputValue) => setSearchJobText(newInputValue)}
                  renderInput={(params) => <TextField {...params} label="Job ID / Name" />}
                  fullWidth
                />
              </Grid>

              {/* Challan Autocomplete */}
              <Grid item xs={12} sm={6}>
                <Autocomplete
                  options={challansList}
                  getOptionLabel={(option) => option.name}
                  filterOptions={(options, { inputValue }) => {
                    if (!inputValue) return options.slice(0, 5);
                    const term = inputValue.toLowerCase();
                    return options.filter((opt) => opt.name.toLowerCase().includes(term));
                  }}
                  value={challansList.find((c) => c._id === challanId) || null}
                  onChange={(e, newValue) => setChallanId(newValue?._id || '')}
                  onInputChange={(e, newInputValue) => setSearchChallanText(newInputValue)}
                  renderInput={(params) => <TextField {...params} label="Challan Name" />}
                  fullWidth
                />
              </Grid>

              {/* Plate Autocomplete */}
              <Grid item xs={12} sm={6}>
                <Autocomplete
                  options={platesList}
                  getOptionLabel={(option) =>
                    `${option.height}X${option.width}X${option.thickness} | ${option.brand?.name || ''} | ${option.technology?.name || ''}`
                  }
                  filterOptions={(options, { inputValue }) => {
                    if (!inputValue) return options.slice(0, 5);
                    const term = inputValue.toLowerCase();
                    return options.filter(
                      (opt) =>
                        opt.brand?.name?.toLowerCase().includes(term) ||
                        opt.technology?.name?.toLowerCase().includes(term)
                    );
                  }}
                  value={platesList.find((p) => p._id === plateId) || null}
                  onChange={(e, newValue) => setPlateId(newValue?._id || '')}
                  onInputChange={(e, newInputValue) => setSearchPlateText(newInputValue)}
                  renderInput={(params) => <TextField {...params} label="Plate" />}
                  fullWidth
                />
              </Grid>

              {/* Customer Company Autocomplete */}
              <Grid item xs={12} sm={6}>
                <Autocomplete
                  options={customerCompaniesList}
                  getOptionLabel={(option) => option.name}
                  filterOptions={(options, { inputValue }) => {
                    if (!inputValue) return options.slice(0, 5);
                    const term = inputValue.toLowerCase();
                    return options.filter((opt) => opt.name.toLowerCase().includes(term));
                  }}
                  value={customerCompaniesList.find((c) => c._id === customerCompanyId) || null}
                  onChange={(e, newValue) => setCustomerCompanyId(newValue?._id || '')}
                  onInputChange={(e, newInputValue) =>
                    setSearchCustomerCompanyText(newInputValue)
                  }
                  renderInput={(params) => <TextField {...params} label="Customer Company" />}
                  fullWidth
                />
              </Grid>

              {/* Date range */}
              <Grid item xs={12} sm={6}>
                <DateTimePicker
                  label="From Date"
                  value={fromDate}
                  onChange={(newValue) => setFromDate(newValue)}
                  renderInput={(params) => <TextField {...params} fullWidth />}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <DateTimePicker
                  label="To Date"
                  value={toDate}
                  onChange={(newValue) => setToDate(newValue)}
                  renderInput={(params) => <TextField {...params} fullWidth />}
                />
              </Grid>

              {/* Type checkboxes */}
              <Grid item xs={12} sm={12}>
                <Typography gutterBottom>Type</Typography>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={type === 'debit'}
                      onChange={(e) => setType(e.target.checked ? 'debit' : '')}
                    />
                  }
                  label="Debit"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={type === 'credit'}
                      onChange={(e) => setType(e.target.checked ? 'credit' : '')}
                    />
                  }
                  label="Credit"
                />
              </Grid>

              <Grid item xs={6}>
                <Button onClick={handleReset} color="error" variant="outlined" fullWidth>
                  Reset All Filter
                </Button>
              </Grid>
              <Grid item xs={6} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button onClick={handleApply} color="success" variant="contained" fullWidth>
                  OK
                </Button>
              </Grid>
            </Grid>
          </Paper>
        </CardContent>
      </Card>
    </Modal>
  );
}